import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { usePrescriptionTemplates } from './hooks/usePrescriptionTemplates';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import PropTypes from 'prop-types';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const TEMPLATE_IMG_STYLE = {
  width: 90,
  height: 127,
  borderRadius: 1,
  objectFit: 'cover',
  border: (theme) => `1px solid ${theme.palette.divider}`,
};

function TemplateUpload({ thumbnail, preview, isLoading, onFileChange, onDelete, label }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
        {isLoading ? (
          <Skeleton variant="rounded" width={90} height={127} />
        ) : preview || thumbnail ? (
          <Box
            component="img"
            src={preview || thumbnail}
            alt={label}
            loading="lazy"
            sx={TEMPLATE_IMG_STYLE}
          />
        ) : (
          <Box
            sx={{
              width: 90,
              height: 127,
              borderRadius: 1,
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.disabled" align="center">
              Sem
              <br />
              template
            </Typography>
          </Box>
        )}
        {preview && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Enviando...
          </Typography>
        )}
      </Box>
      <Stack spacing={1}>
        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} size="small">
          {thumbnail || preview ? 'Alterar' : 'Selecionar'}
          <VisuallyHiddenInput
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={onFileChange}
          />
        </Button>
        {thumbnail && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            size="small"
            onClick={onDelete}
          >
            Remover
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          JPG, PNG ou WebP — máx. 10 MB
        </Typography>
      </Stack>
    </Stack>
  );
}

TemplateUpload.propTypes = {
  thumbnail: PropTypes.string,
  preview: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  onFileChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

TemplateUpload.defaultProps = {
  thumbnail: null,
  preview: null,
};

export default function PrescriptionTemplate() {
  const navigate = useNavigate();
  const {
    fetchClinic,
    fetchDentists,
    fetchTemplatesFromClinic,
    fetchTemplatesFromDentists,
    updateMode,
    uploadClinicTemplate,
    uploadDentistTemplate,
    deleteTemplate,
  } = usePrescriptionTemplates();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [mode, setMode] = useState(null);
  const [clinicId, setClinicId] = useState(null);

  // clinic template
  const [clinicPrescriptionTemplateThumbnail, setClinicPrescriptionTemplateThumbnail] =
    useState(null);
  const [clinicPreview, setClinicPreview] = useState(null);
  const [isClinicLoading, setClinicLoading] = useState(true);
  const [clinicTemplateId, setClinicTemplateId] = useState(null);

  // dentist templates
  const [dentists, setDentists] = useState([]);
  const [isDentistsLoading, setDentistsLoading] = useState(false);
  const [dentistThumbnails, setDentistThumbnails] = useState({});
  const [dentistPreviews, setDentistPreviews] = useState({});
  const [dentistTemplateIds, setDentistTemplateIds] = useState({});

  // delete confirmation
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetLabel, setDeleteTargetLabel] = useState('');

  const getMode = useCallback(() => {
    fetchClinic()
      .then((res) => {
        const apiMode = res.data?.prescriptionTemplateMode;
        setMode(apiMode === 'PER_DENTIST' ? 'dentist' : 'clinic');
        setClinicId(res.data?.id ?? null);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        setMode('clinic');
      });
  }, [navigate, fetchClinic]);

  const getClinicPrescriptionTemplateFromClinic = useCallback(() => {
    if (!clinicId) return;
    setClinicLoading(true);
    fetchTemplatesFromClinic(clinicId)
      .then((res) => {
        setClinicPrescriptionTemplateThumbnail(res.data?.thumbnailUrl ?? null);
        setClinicTemplateId(res.data?.id ?? null);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setClinicLoading(false));
  }, [clinicId, navigate, fetchTemplatesFromClinic]);

  const getDentists = useCallback(() => {
    setDentistsLoading(true);
    fetchDentists()
      .then((res) => setDentists(res.data ?? []))
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setDentistsLoading(false));
  }, [navigate, fetchDentists]);

  const getClinicPrescriptionTemplateFromDentists = useCallback(() => {
    fetchTemplatesFromDentists()
      .then((res) => {
        const templates = res.data ?? [];
        const thumbnails = {};
        const templateIds = {};
        templates.forEach((t) => {
          thumbnails[t.dentistId] = t.thumbnailUrl;
          templateIds[t.dentistId] = t.id;
        });
        setDentistThumbnails(thumbnails);
        setDentistTemplateIds(templateIds);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  }, [navigate, fetchTemplatesFromDentists]);

  const handleModeChange = (_, newMode) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    updateMode(newMode === 'dentist' ? 'PER_DENTIST' : 'CLINIC').catch((returnFromApi) => {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
      console.error('ops! ocorreu um erro' + returnFromApi);
      if (returnFromApi.status === 401) {
        navigate('/expired-session');
      }
      setMode(mode);
    });
  };

  useEffect(() => {
    getMode();
  }, [getMode]);

  useEffect(() => {
    if (clinicId) getClinicPrescriptionTemplateFromClinic();
  }, [clinicId, getClinicPrescriptionTemplateFromClinic]);

  useEffect(() => {
    if (mode === 'dentist') {
      getDentists();
      getClinicPrescriptionTemplateFromDentists();
    }
  }, [mode, getDentists, getClinicPrescriptionTemplateFromDentists]);

  const validateFile = (file, event) => {
    const MAX_SIZE_BYTES = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      setShowErrorMessage(true);
      setErrorMessage('A imagem excede o limite de 10 MB.');
      event.target.value = '';
      return false;
    }
    if (!file.type.startsWith('image/')) {
      setShowErrorMessage(true);
      setErrorMessage('Selecione um arquivo de imagem válido (JPG, PNG, etc.).');
      event.target.value = '';
      return false;
    }
    return true;
  };

  const handleClinicPrescriptionTemplateToClinicFileChange = (event) => {
    const file = event.target.files[0];
    if (!file || !validateFile(file, event)) return;

    const reader = new FileReader();
    reader.onloadend = () => setClinicPreview(reader.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('clinicId', clinicId);
    formData.append('prescriptionTemplateFile', file);
    setLoading(true);
    uploadClinicTemplate(formData)
      .then(() => {
        setClinicPreview(null);
        getClinicPrescriptionTemplateFromClinic();
        setShowSuccessMessage(true);
        setSuccessMessage('Template da clínica enviado com sucesso!');
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        setClinicPreview(null);
      })
      .finally(() => setLoading(false));
  };

  const handleClinicPrescriptionTemplateToDentistFileChange = (dentistId) => (event) => {
    const file = event.target.files[0];
    if (!file || !validateFile(file, event)) return;

    const reader = new FileReader();
    reader.onloadend = () =>
      setDentistPreviews((prev) => ({ ...prev, [dentistId]: reader.result }));
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('clinicId', clinicId);
    formData.append('dentistId', dentistId);
    formData.append('prescriptionTemplateFile', file);
    setLoading(true);
    uploadDentistTemplate(formData)
      .then(() => {
        setDentistPreviews((prev) => ({ ...prev, [dentistId]: null }));
        getClinicPrescriptionTemplateFromDentists();
        setShowSuccessMessage(true);
        setSuccessMessage('Template do dentista enviado com sucesso!');
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
        setDentistPreviews((prev) => ({ ...prev, [dentistId]: null }));
      })
      .finally(() => setLoading(false));
  };

  const confirmDelete = (id, label) => {
    setDeleteTargetId(id);
    setDeleteTargetLabel(label);
  };

  const handleDelete = () => {
    setLoading(true);
    deleteTemplate(deleteTargetId)
      .then(() => {
        setShowSuccessMessage(true);
        setSuccessMessage('Template removido com sucesso!');
        if (clinicTemplateId === deleteTargetId) {
          setClinicPrescriptionTemplateThumbnail(null);
          setClinicTemplateId(null);
        } else {
          const dentistId = Object.keys(dentistTemplateIds).find(
            (k) => dentistTemplateIds[k] === deleteTargetId,
          );
          if (dentistId) {
            setDentistThumbnails((prev) => ({ ...prev, [dentistId]: null }));
            setDentistTemplateIds((prev) => ({ ...prev, [dentistId]: null }));
          }
        }
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => {
        setLoading(false);
        setDeleteTargetId(null);
        setDeleteTargetLabel('');
      });
  };

  return (
    <>
      <Loading isToHidden={!isLoading} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack spacing={3}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Tipo de Template
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {mode === null ? (
            <Skeleton variant="rounded" width={320} height={36} />
          ) : (
            <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
              <ToggleButton value="clinic">
                <Tooltip title="Um único template usado para todos os dentistas">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <BusinessIcon fontSize="small" />
                    <span>Único para a clínica</span>
                  </Stack>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="dentist">
                <Tooltip title="Cada dentista pode ter seu próprio template">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <PersonIcon fontSize="small" />
                    <span>Por dentista</span>
                  </Stack>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {mode === 'clinic' && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Template da Clínica
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Imagem usada como fundo ao imprimir receituários. Recomendado: PNG ou JPG no tamanho
              A4 (210 × 297 mm).
            </Typography>
            <TemplateUpload
              thumbnail={clinicPrescriptionTemplateThumbnail}
              preview={clinicPreview}
              isLoading={isClinicLoading}
              onFileChange={handleClinicPrescriptionTemplateToClinicFileChange}
              onDelete={() => confirmDelete(clinicTemplateId, 'o template da clínica')}
              label="Template da clínica"
            />
          </Box>
        )}

        {mode === 'dentist' && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Templates por Dentista
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Defina um template individual para cada dentista. Ao imprimir, será usado o template
              do dentista responsável pelo receituário.
            </Typography>
            {isDentistsLoading ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rounded" height={48} />
                ))}
              </Stack>
            ) : (
              <Stack spacing={3} divider={<Divider />}>
                {dentists.map((dentist) => (
                  <Stack key={dentist.id} spacing={1.5}>
                    <Typography variant="body2" fontWeight={500}>
                      {dentist.nome}
                    </Typography>
                    <TemplateUpload
                      thumbnail={dentistThumbnails[dentist.id] ?? null}
                      preview={dentistPreviews[dentist.id] ?? null}
                      isLoading={false}
                      onFileChange={handleClinicPrescriptionTemplateToDentistFileChange(dentist.id)}
                      onDelete={() =>
                        confirmDelete(
                          dentistTemplateIds[dentist.id],
                          `o template de ${dentist.nome}`,
                        )
                      }
                      label={`Template de ${dentist.nome}`}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Stack>

      <Dialog open={Boolean(deleteTargetId)} onClose={() => setDeleteTargetId(null)}>
        <DialogTitle>Remover template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover {deleteTargetLabel}? Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
